import { appConfirm } from "@/lib/appDialog";
import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";
import { EmptyState, ErrorState } from "@/components/StateViews";
import { toast } from "sonner";
import { Loader2, Handshake, FileSpreadsheet, FileText, Search, ArrowLeft, X, Download, Trash2 } from "lucide-react";

export default function AssociationSurveysAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = async () => {
    setLoading(true); setError("");
    try {
      const { data } = await api.get(`/admin/association-surveys${q ? `?q=${encodeURIComponent(q)}` : ""}`);
      setItems(data.surveys || []);
    } catch (e) { setError(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { const t = setTimeout(load, 350); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);

  const openDetail = async (id) => {
    setSelectedId(id); setDetail(null);
    try {
      const { data } = await api.get(`/admin/association-surveys/${id}`);
      setDetail(data.survey);
    } catch (e) { toast.error(formatApiError(e)); setSelectedId(null); }
  };
  const downloadCsv = async () => {
    try {
      const res = await api.get("/admin/association-surveys/export/csv", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv;charset=utf-8" }));
      const a = document.createElement("a"); a.href = url; a.download = "emad-association-surveys.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const downloadAllPdf = async () => {
    try {
      const res = await api.get("/admin/association-surveys/export/pdf", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = "emad-association-surveys.pdf"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const downloadOnePdf = async (id) => {
    try {
      const res = await api.get(`/admin/association-surveys/export/pdf/${id}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = `association-survey-${id}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteSurvey = async (id, name) => {
    if (!await appConfirm(`حذف استبيان جمعية «${name || ""}»؟ ستتمكّن الجمعية من إعادة تعبئته لاحقاً.`)) return;
    try {
      await api.delete(`/admin/association-surveys/${id}`);
      toast.success("تم حذف الاستبيان");
      if (selectedId === id) { setSelectedId(null); setDetail(null); }
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <DashboardLayout>
      <div data-testid="admin-assoc-surveys">
        <div className="mb-8 flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-teal-700">استبيانات الجمعيات</div>
            <h1 className="mt-2 text-2xl font-heading font-extrabold text-slate-900">استبيانات الجمعيات الشريكة</h1>
            <p className="mt-2 text-slate-600 max-w-3xl">استعراض احتياجات الجمعيات وجاهزيتها لاستقبال المتدربين. تصدير Excel/PDF لتحليل البيانات.</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={downloadCsv} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-700 text-white text-sm font-semibold hover:bg-emerald-600" data-testid="assoc-export-csv"><FileSpreadsheet className="h-4 w-4" /> Excel (CSV)</button>
            <button onClick={downloadAllPdf} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-rose-700 text-white text-sm font-semibold hover:bg-rose-600" data-testid="assoc-export-all-pdf"><FileText className="h-4 w-4" /> PDF (الكل)</button>
          </div>
        </div>

        <div className="mb-4 relative max-w-md">
          <Search className="h-4 w-4 text-slate-400 absolute inset-y-0 start-3 my-auto" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="بحث بالاسم أو البريد" className="w-full rounded-lg border border-slate-300 bg-white ps-9 pe-3 py-2.5 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none" data-testid="assoc-surveys-search" />
        </div>

        {loading ? (
          <div className="grid place-items-center py-16"><Loader2 className="h-8 w-8 animate-spin text-teal-700" /></div>
        ) : error ? (
          <ErrorState message={error} onRetry={load} testid="assoc-surveys-error" />
        ) : items.length === 0 ? (
          <EmptyState icon={Handshake} title="لا توجد استبيانات جمعيات بعد" description="ستظهر إجابات الجمعيات هنا فور تعبئتها للاستبيان." testid="assoc-surveys-empty" />
        ) : (
          <div className="rounded-2xl bg-white border border-cream-200 card-shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-cream-50">
                  <tr>
                    <th className="text-start px-6 py-3 font-semibold text-slate-700">الجمعية</th>
                    <th className="text-start px-6 py-3 font-semibold text-slate-700">البريد</th>
                    <th className="text-start px-6 py-3 font-semibold text-slate-700">المدينة</th>
                    <th className="text-start px-6 py-3 font-semibold text-slate-700">المجال</th>
                    <th className="text-start px-6 py-3 font-semibold text-slate-700">الحجم</th>
                    <th className="text-start px-6 py-3 font-semibold text-slate-700">قدرة الاستقبال</th>
                    <th className="text-start px-6 py-3 font-semibold text-slate-700">التاريخ</th>
                    <th className="text-start px-6 py-3 font-semibold text-slate-700"></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((s) => (
                    <tr key={s.id} className="border-t border-cream-100 hover:bg-cream-50/60" data-testid={`assoc-row-${s.id}`}>
                      <td className="px-6 py-3 font-semibold text-slate-900">{s.org_name || "—"}</td>
                      <td className="px-6 py-3 text-slate-700" dir="ltr">{s.contact_email || "—"}</td>
                      <td className="px-6 py-3 text-slate-700">{s.city || "—"}</td>
                      <td className="px-6 py-3 text-slate-700">{s.field_of_work || "—"}</td>
                      <td className="px-6 py-3 text-slate-700">{s.size || "—"}</td>
                      <td className="px-6 py-3 text-slate-700">{s.can_host_trainees || "—"}</td>
                      <td className="px-6 py-3 text-slate-500 text-xs arabic-numerals">{s.submitted_at?.slice(0, 10)}</td>
                      <td className="px-6 py-3">
                        <div className="flex items-center gap-1.5">
                          <button onClick={() => openDetail(s.id)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-teal-50 text-teal-700 text-xs font-semibold hover:bg-teal-100" data-testid={`view-assoc-${s.id}`}>عرض <ArrowLeft className="h-3 w-3" /></button>
                          <button onClick={() => downloadOnePdf(s.id)} className="inline-flex items-center gap-1 p-1.5 rounded-lg bg-rose-50 text-rose-700 hover:bg-rose-100" title="تنزيل PDF" data-testid={`pdf-assoc-${s.id}`}><Download className="h-3.5 w-3.5" /></button>
                          <button onClick={() => deleteSurvey(s.id, s.org_name)} className="inline-flex items-center gap-1 p-1.5 rounded-lg bg-red-50 text-red-700 hover:bg-red-100" title="حذف الاستبيان" data-testid={`delete-assoc-${s.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {selectedId && (
        <DetailModal detail={detail} onClose={() => { setSelectedId(null); setDetail(null); }} onDownloadPdf={() => downloadOnePdf(selectedId)} />
      )}
    </DashboardLayout>
  );
}

function DetailModal({ detail, onClose, onDownloadPdf }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm grid place-items-center p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="w-full max-w-3xl bg-white rounded-2xl shadow-2xl border border-slate-200 max-h-[92vh] flex flex-col" data-testid="assoc-detail-modal">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h3 className="font-heading font-bold text-slate-900 text-lg">{detail?.org_name || "استبيان الجمعية"}</h3>
            <div className="text-xs text-slate-500" dir="ltr">{detail?.contact_email}</div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onDownloadPdf} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-rose-700 text-white text-xs font-semibold hover:bg-rose-600"><Download className="h-3.5 w-3.5" /> PDF</button>
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100"><X className="h-5 w-5" /></button>
          </div>
        </div>
        <div className="p-4 overflow-y-auto space-y-3">
          {!detail ? (
            <div className="grid place-items-center py-8"><Loader2 className="h-8 w-8 animate-spin text-teal-700" /></div>
          ) : (
            Object.entries(detail.labels || {}).map(([k, label]) => {
              const v = detail.answers[k];
              if (v === undefined || v === null || v === "") return null;
              const shown = Array.isArray(v) ? v.join("، ") : typeof v === "boolean" ? (v ? "نعم" : "لا") : v;
              return (
                <div key={k} className="flex items-start justify-between gap-4 py-2 border-b border-cream-100 last:border-0">
                  <div className="text-sm text-slate-600">{label}</div>
                  <div className="text-sm font-semibold text-slate-900 text-end arabic-numerals">{shown}</div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
